import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { AppUser, AppSession } from '@/types/auth';
import { sanitizeInput, isValidEmail, rateLimiter } from '@/utils/securityUtils';
import { appQueryClient } from '@/utils/queryConfig';
import {
  getLiberiaAuthSession,
  getLiberiaCurrentUser,
  loginLiberiaAuth,
  logoutLiberiaAuth,
  registerLiberiaAuth,
} from '@/utils/liberiaApi';
import { fetchModuleConfig } from '@/config/modules';

interface AuthContextType {
  user: AppUser | null;
  session: AppSession | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string, countryCode?: string) => Promise<{ error: any }>;
  isQlsAdmin: boolean;
  countryId: string | null;
  countryCode: string | null;
  signIn: (email: string, password: string) => Promise<{
    error: any;
    mfaRequired?: boolean;
    challengeToken?: string;
    email?: string;
  }>;
  completeMfaSignIn: (authSession: Awaited<ReturnType<typeof loginLiberiaAuth>>) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  isSessionValid: boolean;
  forceSessionCheck: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function buildBackendUser(currentUser: any): AppUser {
  return {
    id: currentUser.id,
    email: currentUser.email || undefined,
    aud: 'authenticated',
    app_metadata: {},
    user_metadata: {
      full_name: currentUser.fullName || undefined,
      primary_role: currentUser.primaryRole || undefined,
      roles: currentUser.roles || [],
      must_change_password: Boolean(currentUser.mustChangePassword),
      country_id: currentUser.countryId || undefined,
      country_code: currentUser.countryCode || undefined,
      is_qls_admin: Boolean(currentUser.isQlsAdmin),
      active_country_id: currentUser.activeCountryId || undefined,
    },
    created_at: new Date().toISOString(),
  } as AppUser;
}

function buildBackendSession(user: AppUser, expiresAt: string | null): AppSession {
  const expiresAtSeconds = expiresAt
    ? Math.floor(new Date(expiresAt).getTime() / 1000)
    : Math.floor(Date.now() / 1000) + 24 * 60 * 60;

  return {
    access_token: 'qls-backend-session',
    token_type: 'bearer',
    expires_in: Math.max(0, expiresAtSeconds - Math.floor(Date.now() / 1000)),
    expires_at: expiresAtSeconds,
    refresh_token: 'qls-backend-session',
    user,
  } as AppSession;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [session, setSession] = useState<AppSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSessionValid, setIsSessionValid] = useState(false);
  // We deliberately use the module-level `appQueryClient` singleton instead of
  // `useQueryClient()` here, because `AuthProvider` is mounted *outside* the
  // `<QueryClientProvider>` in `App.tsx` (so the hook would throw). The same
  // singleton is passed into `<QueryClientProvider>`, so mutating it here
  // (e.g. `.clear()` on sign-in / sign-out) is observed by every component
  // inside the React tree. Without this wipe, a soft sign-in / sign-out
  // leaves the cache populated with data from the wrong user (or the empty
  // `[]` placeholders fetched before auth resolved), which is what was
  // forcing users to hard-refresh after logging in.
  const queryClient = appQueryClient;

  const clearAuthState = useCallback(() => {
    setUser(null);
    setSession(null);
    setIsSessionValid(false);
  }, []);

  const applyBackendSession = useCallback(async () => {
    const sessionData = await getLiberiaAuthSession();

    if (!sessionData.authenticated || !sessionData.isSessionValid || !sessionData.userId) {
      clearAuthState();
      return false;
    }

    const currentUser = await getLiberiaCurrentUser({ user: null, role: null, hasAddressGenerationAccess: false });
    const backendUser = buildBackendUser(currentUser);
    const backendSession = buildBackendSession(backendUser, sessionData.expiresAt);

    setUser(backendUser);
    setSession(backendSession);
    setIsSessionValid(true);
    const signedInCountryId = currentUser.activeCountryId || currentUser.countryId || null;
    if (signedInCountryId) {
      await fetchModuleConfig(signedInCountryId);
    }
    return true;
  }, [clearAuthState]);

  const forceSessionCheck = useCallback(async () => {
    try {
      await applyBackendSession();
    } catch (error) {
      console.error('QLS auth session check failed:', error);
      clearAuthState();
    }
  }, [applyBackendSession, clearAuthState]);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await applyBackendSession();
      } catch (error) {
        console.error('Failed to initialize QLS auth session:', error);
        clearAuthState();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [applyBackendSession, clearAuthState]);

  const signOut = async () => {
    try {
      try {
        await logoutLiberiaAuth();
      } catch (error) {
        console.error('QLS sign out failed:', error);
      }

      try {
        localStorage.setItem('qls_last_logout', Date.now().toString());
      } catch {}

      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i += 1) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('sb-') || key.includes('auth'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
        sessionStorage.clear();
      } catch {}

      clearAuthState();

      // Wipe every cached query so the next signed-in user (or the next
      // unauthenticated visit) doesn't see anything fetched under the prior
      // session. Mirrors the sessionStorage.clear() above for React Query.
      try {
        queryClient.clear();
      } catch {}

      return { error: null };
    } catch (error) {
      console.error('SignOut error:', error);
      clearAuthState();
      try {
        queryClient.clear();
      } catch {}
      return { error: null };
    }
  };

  const signUp = async (email: string, password: string, fullName?: string, countryCode?: string) => {
    if (!email || !password) {
      return { error: { message: 'Email and password are required' } };
    }

    if (!isValidEmail(email)) {
      return { error: { message: 'Please enter a valid email address' } };
    }

    if (password.length < 8) {
      return { error: { message: 'Password must be at least 8 characters long' } };
    }

    if (!countryCode) {
      return { error: { message: 'Select the country you are mapping in.' } };
    }

    try {
      const authSession = await registerLiberiaAuth(email, password, fullName, countryCode);
      if (authSession.mfaRequired) {
        return { error: { message: 'Account created. Sign in to continue.' } };
      }
      return finalizeAuthenticatedSession(authSession, email);
    } catch (error: any) {
      return { error: { message: error?.message || 'Sign up failed' } };
    }
  };

  const finalizeAuthenticatedSession = async (
    authSession: Awaited<ReturnType<typeof loginLiberiaAuth>>,
    emailForRateLimit?: string
  ) => {
    const currentUser = authSession.user;

    if (!currentUser) {
      return { error: { message: 'Login failed. No user was returned.' } };
    }

    const sessionVerify = await getLiberiaAuthSession();
    if (!sessionVerify.authenticated || !sessionVerify.userId) {
      return {
        error: {
          message:
            'Login succeeded but your browser did not keep the session cookie. Allow cookies for this site (or disable strict cross-site blocking) and try again.',
        },
      };
    }

    const backendUser = buildBackendUser(currentUser);
    const backendSession = buildBackendSession(backendUser, authSession.expiresAt);

    try {
      queryClient.clear();
    } catch {}

    try {
      sessionStorage.removeItem('qls_selected_role');
      const redirectFlagKeys: string[] = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('qls_redirect_done')) {
          redirectFlagKeys.push(key);
        }
      }
      redirectFlagKeys.forEach((key) => sessionStorage.removeItem(key));
    } catch {}

    setUser(backendUser);
    setSession(backendSession);
    setIsSessionValid(true);
    try {
      localStorage.removeItem('qls_last_logout');
    } catch {}
    if (emailForRateLimit) {
      rateLimiter.reset(`signin_${emailForRateLimit}`);
    }
    return { error: null };
  };

  const signIn = async (email: string, password: string) => {
    if (!email || !password) {
      return { error: { message: 'Email and password are required' } };
    }

    if (!isValidEmail(email)) {
      return { error: { message: 'Please enter a valid email address' } };
    }

    if (!rateLimiter.isAllowed(`signin_${email}`, 5, 900000)) {
      return { error: { message: 'Too many login attempts. Please try again later.' } };
    }

    const sanitizedEmail = sanitizeInput(email.toLowerCase().trim());

    try {
      const authSession = await loginLiberiaAuth(sanitizedEmail, password);

      if (authSession.mfaRequired && authSession.challengeToken) {
        rateLimiter.reset(`signin_${email}`);
        return {
          error: null,
          mfaRequired: true,
          challengeToken: authSession.challengeToken,
          email: authSession.user?.email || sanitizedEmail,
        };
      }

      return finalizeAuthenticatedSession(authSession, email);
    } catch (error: any) {
      console.error('QLS sign-in failed:', error);
      return { error: { message: error?.message || 'Login failed. Please check your credentials.' } };
    }
  };

  const completeMfaSignIn = async (authSession: Awaited<ReturnType<typeof loginLiberiaAuth>>) => {
    return finalizeAuthenticatedSession(authSession);
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    completeMfaSignIn,
    signOut,
    isSessionValid,
    forceSessionCheck,
    isQlsAdmin: Boolean(user?.user_metadata?.is_qls_admin),
    countryId: (user?.user_metadata?.active_country_id || user?.user_metadata?.country_id || null) as string | null,
    countryCode: (user?.user_metadata?.country_code || null) as string | null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
